import { environnement } from "configs/environnement";
import api from "./api";
import {
  ISaveMemberDTO,
  IUpdateMemberDTO,
  IMemberResponseDTO,
  IMemberPageResponse,
} from "models/church.types";

const RESOURCE_URL = `${environnement.baseURL}/members`;

export const MemberService = {
  /**
   * Crée un nouveau membre (SaveMemberDTO)
   */
  create: async (member: ISaveMemberDTO): Promise<IMemberResponseDTO> => {
    const response = await api.post<IMemberResponseDTO>(RESOURCE_URL, member);
    return response.data;
  },

  /**
   * Met à jour un membre existant (UpdateMemberDTO)
   */
  update: async (member: IUpdateMemberDTO): Promise<IMemberResponseDTO> => {
    const response = await api.put<IMemberResponseDTO>(RESOURCE_URL, member);
    return response.data;
  },

  /**
   * Récupère un membre par son ID unique
   */
  getById: async (id: number): Promise<IMemberResponseDTO> => {
    const response = await api.get<IMemberResponseDTO>(`${RESOURCE_URL}/${id}`);
    return response.data;
  },

  /**
   * Supprime un membre par son ID
   */
  delete: async (id: number): Promise<void> => {
    await api.delete(`${RESOURCE_URL}/${id}`);
  },

  /**
   * Recherche unifiée et multicritères de membres (Elasticsearch + JPA Pivots)
   * Permet de lister, filtrer par département, classe Ecodime, statut enfant ou texte libre.
   * * @param query Chaîne de caractères recherchée (Nom, prénom, email, téléphone...)
   * @param page Index de la page (commence à 0)
   * @param size Nombre d'éléments par page
   * @param filters Filtres optionnels basés sur les pivots structurels du backend
   */
  search: async (
    query: string = "",
    page: number,
    size: number,
    filters?: {
      departmentId?: number | string | null;
      ecodimeClassId?: number | string | null;
      isChild?: boolean | null;
    },
  ): Promise<IMemberPageResponse> => {
    const response = await api.get<IMemberResponseDTO[]>(
      `${RESOURCE_URL}/_search`,
      {
        params: {
          query: query.trim() || undefined, // transmet undefined si vide pour épurer la requête
          departmentId: filters?.departmentId || undefined,
          ecodimeClassId: filters?.ecodimeClassId || undefined,
          isChild:
            filters?.isChild !== undefined && filters?.isChild !== null
              ? filters.isChild
              : undefined,
          page: page,
          size: size,
          sort: "id,desc", // Comportement par défaut : derniers inscrits en premier
        },
      },
    );

    return {
      data: response.data,
      totalCount: parseInt(response.headers["x-total-count"] || "0", 10),
    };
  },

  /**
   * Alias de compatibilité pour le Tableau de bord ou les composants existants.
   * Émule la récupération de l'ensemble de la liste des membres via l'endpoint unifié.
   */
  getAll: async (page: number, size: number): Promise<IMemberPageResponse> => {
    return MemberService.search("", page, size);
  },

  /**
   * Alias de compatibilité pour filtrer l'affichage par département via l'endpoint unifié.
   */
  getByDepartment: async (
    deptId: number | string,
    page: number,
    size: number,
  ): Promise<IMemberPageResponse> => {
    return MemberService.search("", page, size, { departmentId: deptId });
  },
};
